import { createSlice } from "@reduxjs/toolkit";
import toast from "react-hot-toast";
import { v4 as uuidv4 } from "uuid";


const initialState = {
    type: "course_wise_pay",
    courseItems: [],
    persons: [],
    totalAmount: 0,
    totalToPay: 0,
    totalSavings: 0,
    totalServiceCharge: 0,
    receivedAmount: 0,
    remainingAmount: 0,
    id: null,
    is_complete: false,
    isLoading: false,
};

const defaultTips = {
    type: "percentage",
    inputAmount: 0,
    amount: 0,
};

const calculateSavingsShare = (state, personTotalItemPrice) => {
    const totalAmount = Number(parseFloat(state.totalAmount || 0).toFixed(2));
    const totalSavings = Number(parseFloat(state.totalSavings || 0).toFixed(2));
    if (totalAmount <= 0 || totalSavings <= 0) return 0;
    return Number(((totalSavings * personTotalItemPrice) / totalAmount).toFixed(2));
};

function withVat(price, vat){
    const settings = JSON.parse(localStorage.getItem("settings") || null);
    if(settings && settings?.take_order?.vat_settings && settings?.take_order?.vat_settings?.is_vat_enabled && settings?.take_order?.vat_settings?.type){
        const vatType = settings?.take_order?.vat_settings?.type 
        if(vatType == "exclusive"){
            return(Number.parseFloat(price) + Number.parseFloat(vat))
        }else{
            return(Number.parseFloat(price))
        }
    }else{
        return(Number.parseFloat(price))
    }
}

const updatePersonSavings = (state) => {
    state.persons.forEach((person) => {
        person.savingsAmount = calculateSavingsShare(state, Number(person.TotalItemPrice || 0));
    });
};

const setInitialState = (state) => {
    if (state.persons.length === 0) {
        state.persons.push({ id: uuidv4(), name: "Person 1", courses: [], slots: [], amountToPay: 0, serviceCharge: 0, amountPaid: 0, TotalItemPrice: 0, savingsAmount: 0, tips: { ...defaultTips } });
        state.persons.push({ id: uuidv4(), name: "Person 2", courses: [], slots: [], amountToPay: 0, serviceCharge: 0, amountPaid: 0, TotalItemPrice: 0, savingsAmount: 0, tips: { ...defaultTips } });
    }
    return state;
}

setInitialState(initialState);

const courseWisePay = createSlice({
  name: "course_wise_pay",
  initialState: initialState,
  reducers: {
        setCourseItems: (state, action) => {
            state.courseItems = action.payload;
            let totalAmount = state.courseItems.reduce((sum, item) => {
                const price = parseFloat(withVat(item.calculated_price, item.vat)) || 0;
                return sum + price;
            }, 0);
            state.receivedAmount = 0;
            state.totalAmount = Number(parseFloat(totalAmount).toFixed(2));
            state.totalToPay = Number(parseFloat(state.totalServiceCharge) + parseFloat(totalAmount) - parseFloat(state.totalSavings)).toFixed(2);
            state.remainingAmount = Number(parseFloat(state.totalServiceCharge) + parseFloat(totalAmount) - parseFloat(state.totalSavings)).toFixed(2);
            updatePersonSavings(state);
            return state;
        },
        addPerson: (state, action) => {
            const newServiceCharge = Number((state.totalServiceCharge / (state.persons.length + 1)).toFixed(2));
            console.log("Adding person with new service charge:", newServiceCharge);
            state.persons.push({ id: uuidv4(), name: "Person " + (state.persons.length + 1), courses: [], slots: [], amountToPay: newServiceCharge, serviceCharge: newServiceCharge, amountPaid: 0, TotalItemPrice: 0, savingsAmount: 0, tips: { ...defaultTips } });
            // remove old persons service charge and add new service charge
            state.persons.forEach(person => {
                if (person.slots.length === 0) {
                    person.amountToPay = Number(parseFloat(person.amountToPay - person.serviceCharge + newServiceCharge).toFixed(2));
                    person.serviceCharge = newServiceCharge;
                }   
            });
            updatePersonSavings(state);
            return state;
        },
        removePerson: (state, action) => {
            const newServiceCharge = Number((state.totalServiceCharge / (state.persons.length - 1 || 1)).toFixed(2));
            state.persons = state.persons.filter(person => person.id !== action.payload);
            // remove old persons service charge and add new service charge
            state.persons.forEach(person => {
                if (person.slots.length === 0) {
                    person.amountToPay = Number(parseFloat(person.amountToPay - person.serviceCharge + newServiceCharge).toFixed(2));
                    person.serviceCharge = newServiceCharge;
                }   
            });
            updatePersonSavings(state);
            return state;
        },
        updatePersonName: (state, action) => {
            const { personId, name } = action.payload || {};    
            const person = state.persons.find(p => p.id === personId);
            if (person) {
                person.name = name;
            }
        },
        moveItemsToPerson: (state, action) => {
        // payload: { personId: string, items: Array }
        const { personId, items } = action.payload || {};
        if (!personId || !Array.isArray(items) || items.length === 0) return state;

        // Find the person and add items to their courses array, update amountToPay
        const person = state.persons.find(p => p.id === personId);
        if (person) {
            if( person.slots.length > 0 ) {
                toast.error(`Cannot add items to ${person.name}. Payments have been made.`);
                return state;
            }

            // Remove items from courseItems by front_item_id
            const itemIds = new Set(items.map(i => i.front_item_id));
            state.courseItems = state.courseItems.filter(ci => !itemIds.has(ci.front_item_id));


            person.courses = person.courses.concat(items.map(i => ({ ...i })));

            const addedAmount = items.reduce((sum, it) => {
            const price = parseFloat(withVat(it.calculated_price, it.vat)) || 0;
            return sum + price;
            }, 0);

            
            person.TotalItemPrice = Number(parseFloat((person.TotalItemPrice || 0) + parseFloat(addedAmount)).toFixed(2));

            if(person?.serviceCharge == 0){
                person.serviceCharge = Number((state.totalServiceCharge / state.persons.length).toFixed(2));
                person.amountToPay = Number(parseFloat((person.amountToPay || 0) + parseFloat(addedAmount) + person.serviceCharge - person.savingsAmount).toFixed(2));
            }else{
                person.amountToPay = Number(parseFloat((person.amountToPay || 0) + parseFloat(addedAmount) - person.savingsAmount).toFixed(2));
            }


            updatePersonSavings(state);

            toast.success("Items assigned to person.");
        }

        return state;
        },
        movePersonItemsToCourseItems: (state, action) => {
        // payload: personId
        const personId = action.payload;
            const person = state.persons.find(p => p.id === personId);
            if (person) {
                if( person.slots.length > 0 ) {
                    toast.error(`Cannot move items back to Course Items. Payments have been made for ${person.name}`);
                    return state;
                }
            // Move all items from person's courses back to courseItems
                state.courseItems = state.courseItems.concat(person.courses.map(i => ({ ...i })));
                person.courses = [];
                person.TotalItemPrice = 0;
                person.amountToPay = person.serviceCharge;
                updatePersonSavings(state);

                toast.success(`All items moved back from ${person.name} to Course Items`);
            }
        return state;
        },
        movePersonItemToCourseItems: (state, action) => {
            // payload: { personId, front_item_id }
            const { personId, front_item_id } = action.payload || {};
            if (!personId || !front_item_id) return state;
            const person = state.persons.find(p => p.id === personId);
            if (!person) return state;
            
            if( person.slots.length > 0 ) {
                toast.error(`Cannot move item back to Course Items. Payments have been made for ${person.name}`);
                return state;
            }
            const itemIndex = person.courses.findIndex(i => i.front_item_id === front_item_id);
            if (itemIndex === -1) return state;

            const [item] = person.courses.splice(itemIndex, 1);
            // Add item back to courseItems
            state.courseItems.push({ ...item });

            // Adjust amountToPay
            const price = parseFloat(item.calculated_price ?? item.base_price ?? 0) || 0;
            person.TotalItemPrice = Number(Math.max(0, (person.TotalItemPrice || 0) - price).toFixed(2));
            person.amountToPay = Number(Math.max(0, (person.amountToPay || 0) - price).toFixed(2));
            updatePersonSavings(state);

            toast.success(`Moved ${item.name} back to Course Items from ${person.name}`);
            return state;
        },

        addCourseWisePayPersonPaymentSlot: (state, action) => {
            const { personId, slot } = action.payload || {};
            const person = state.persons.find(p => p.id === personId);
            if (person) {
                // console.log("---->>>>> amountPaid", Number(parseFloat(person.amountPaid).toFixed(2)));
                // console.log("---->>>>> new slot.amount", Number(parseFloat(slot.amount).toFixed(2)));
                // console.log("---->>>>> new total", Number(Number(parseFloat(person.amountPaid).toFixed(2)) + Number(parseFloat(slot.amount).toFixed(2))).toFixed(2));
                // console.log("---->>>>> total to pay", parseFloat(person.amountToPay).toFixed(2))
                
                if( Number(Number(parseFloat(person.amountPaid).toFixed(2)) + Number(parseFloat(slot.amount).toFixed(2))).toFixed(2) > Number(person.amountToPay) ) {
                    toast.error(`Payment exceeds amount to pay for ${person.name}`);
                    return state;
                }

                person.slots.push(slot);

                state.receivedAmount = Number((parseFloat(state.receivedAmount) + parseFloat(slot.amount)).toFixed(2));
                state.remainingAmount = Number((state.totalToPay - state.receivedAmount).toFixed(2));

                person.amountPaid = Number(parseFloat(person.amountPaid).toFixed(2)) + Number(parseFloat(slot.amount).toFixed(2));
            }

            return state;
        },
        setCourseWisePayPersonTips: (state, action) => {
            const { personId, tips } = action.payload || {};
            const person = state.persons.find(p => p.id === personId);
            if (!person || !tips) return state;

            const previousTipAmount = Number(parseFloat(person.tips?.amount || 0).toFixed(2));
            const nextTipAmount = Number(parseFloat(tips.amount || 0).toFixed(2));

            person.amountToPay = Number(
                Math.max(0, (person.amountToPay || 0) - previousTipAmount + nextTipAmount).toFixed(2)
            );

            person.tips = {
                type: tips.type || "percentage",
                inputAmount: Number(parseFloat(tips.inputAmount || 0).toFixed(2)),
                amount: nextTipAmount,
            };

            return state;
        },

        emptyCourseWisePayState: (state) => {
        state.courseItems = [];
        state.persons = [];
        state.totalAmount = 0;
        state.totalSavings = 0;
        state.totalServiceCharge = 0;
        state.receivedAmount = 0;
        state.remainingAmount = 0;
        state.id = null;
        state.is_complete = false;
        return state;
        },
        setCourseWisePayTotals: (state, action) => {
            state.totalSavings = Number(parseFloat(action?.payload?.totalSavings || 0).toFixed(2));
            state.totalServiceCharge = Number(parseFloat(action?.payload?.totalServiceCharge || 0).toFixed(2));
            updatePersonSavings(state);
            return state;
        },
        setInitialCourseWisePayState: (state) => {
            return setInitialState(state);
        },
        setCourseWisePayToReduxState: (state, action) => {
            // state = action.payload;
            // return state;

            // const formattedData = {id:null, courseItems: [], persons: [], totalAmount: 0, receivedAmount: 0, remainingAmount: 0};

            // formattedData.id = action.payload.id || null;
            // formattedData.courseItems = action.payload.courseItems || [];
            // formattedData.persons = action.payload.persons || [];
            // formattedData.totalAmount = parseFloat(action?.payload?.total_amount) || 0;
            // formattedData.receivedAmount = parseFloat(action?.payload?.received_amount) || 0;
            // formattedData.remainingAmount = parseFloat(action?.payload?.remaining_amount) || 0;

            console.log("Setting Course Wise Pay Redux State with payload:", action?.payload);

            // const addedAmount = items.reduce((sum, it) => {
            // const price = parseFloat(withVat(it.calculated_price, it.vat)) || 0;
            // return sum + price;
            // }, 0);

            state.id = action?.payload?.id;
            state.courseItems = action?.payload?.courseItems;
            state.persons = (action?.payload?.persons || []).map(person => ({
                ...person,
                TotalItemPrice:  person.courses.reduce((sum, it) => {
                    const price = parseFloat(withVat(it.calculated_price, it.vat)) || 0;
                    return sum + price;
                    }, 0),
                serviceCharge: Number(person?.service_charge_amount || 0),
                tips: person.tips ? {
                    type: person.tips.type || "percentage",
                    inputAmount: Number(parseFloat(person.tips.inputAmount || 0).toFixed(2)),
                    amount: Number(parseFloat(person.tips.amount || 0).toFixed(2)),
                } : { ...defaultTips },
            }));
            state.totalAmount = Number(action?.payload?.total_with_vat);
            state.totalSavings = Number(parseFloat(action?.payload?.total_savings || state.totalSavings || 0).toFixed(2));
            state.receivedAmount = Number(action?.payload?.received_amount);
            state.remainingAmount = Number(action?.payload?.remaining_amount);
            state.is_complete = action?.payload?.is_completed || false;
            // updatePersonSavings(state);

            return state;   
            
        },
        markCourseWisePayAsComplete: (state) => {
            state.is_complete = true;
            return state;
        },
        updateCourseWisePayLoadingState: (state, action) => {
            state.isLoading = action.payload;
            return state;
        }

    },
});

export const { setCourseItems, addPerson, removePerson, updatePersonName, moveItemsToPerson, movePersonItemsToCourseItems, movePersonItemToCourseItems, addCourseWisePayPersonPaymentSlot, setCourseWisePayPersonTips, emptyCourseWisePayState, setCourseWisePayTotals, setInitialCourseWisePayState, setCourseWisePayToReduxState, markCourseWisePayAsComplete, updateCourseWisePayLoadingState } = courseWisePay.actions;
export default courseWisePay.reducer;
